import { type Dispatch, type SetStateAction, useRef, useState } from "react";
import { DelayedPromise } from "shared/helpers/DelayedPromise";

export type PillarModalControl = {
  isOpen: boolean;
  onClose: () => Promise<void>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export type PillarModalControlAsync<T = void> = PillarModalControl & {
  openAsync: () => Promise<T>;
  resolve: (value: T) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const usePillarModalControl = <T extends unknown = void>({
  onCloseCallback,
}: {
  onCloseCallback?: () => Promise<void> | void;
} = {}): PillarModalControlAsync<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const pendingPromiseRef = useRef<DelayedPromise<T> | null>(null);

  const onClose = async () => {
    await onCloseCallback?.();
    setIsOpen(false);
    if (pendingPromiseRef.current) {
      pendingPromiseRef.current.reject("modal closed");
      pendingPromiseRef.current = null;
    }
  };

  const openAsync = (): Promise<T> => {
    if (pendingPromiseRef.current) {
      pendingPromiseRef.current.reject("modal reopened");
    }
    const delayed = new DelayedPromise<T>();
    pendingPromiseRef.current = delayed;
    setIsOpen(true);
    return delayed.promise;
  };

  const resolve = (value: T) => {
    if (pendingPromiseRef.current) {
      pendingPromiseRef.current.resolve(value);
      pendingPromiseRef.current = null;
    }
    setIsOpen(false);
  };

  return {
    isOpen,
    onClose,
    setIsOpen,
    openAsync,
    resolve,
  };
};
